const focusableSelector = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
].join(',');

const isVisible = (element) => Boolean(
  element
  && (element.offsetWidth || element.offsetHeight || element.getClientRects().length),
);

const getFocusableElements = (rootElement) => (
  Array.from(rootElement.querySelectorAll(focusableSelector))
    .filter((element) => isVisible(element) && element.getAttribute('aria-hidden') !== 'true')
);

const calendarOverlaySelector = [
  '.rmdp-container .rmdp-wrapper',
  '.rmdp-container .rmdp-calendar',
  '.rmdp-wrapper',
  '.rmdp-calendar',
].join(', ');

const getActiveCalendarOverlay = () => (
  Array.from(document.querySelectorAll(calendarOverlaySelector))
    .find((overlayElement) => isVisible(overlayElement))
);

const keyboardTabbableSelector = [
  '.rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled)',
  '.rmdp-left',
  '.rmdp-right',
  '.rmdp-header-values span',
].join(',');

const keydownHandledAttribute = 'data-keydown-added';

const focusFirstVisibleElement = (selector, parentElement = document) => {
  const firstElement = parentElement.querySelector(selector);
  if (firstElement && isVisible(firstElement)) {
    firstElement.focus();
  }
};

const activateDatePickerElement = (element) => {
  const isHeaderValue = element.parentElement
    && element.parentElement.classList.contains('rmdp-header-values');
  const isMonthOption = Boolean(element.closest('.rmdp-month-picker'));
  const isYearOption = Boolean(element.closest('.rmdp-year-picker'));

  element.click();

  if (isHeaderValue) {
    setTimeout(() => {
      const spans = element.parentElement.querySelectorAll('span');
      const spanIndex = Array.from(spans).indexOf(element);
      if (spanIndex === 0) {
        focusFirstVisibleElement('.rmdp-month-picker .rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled)');
      } else if (spanIndex === 1) {
        focusFirstVisibleElement('.rmdp-year-picker .rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled)');
      }
    }, 100);
    return;
  }

  if (isMonthOption || isYearOption) {
    setTimeout(() => {
      focusFirstVisibleElement('.rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled)');
    }, 100);
  }
};

function onDatePickerElementKeyDown(element, event) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  event.preventDefault();
  activateDatePickerElement(element);
}

export const ensureKeyboardTabbableElements = (calendarOverlayElement) => {
  const hiddenDayElements = Array.from(calendarOverlayElement.querySelectorAll('.rmdp-day.rmdp-day-hidden'));
  hiddenDayElements.forEach((element) => {
    element.removeAttribute('tabindex');
  });

  const disabledElements = Array.from(calendarOverlayElement.querySelectorAll('.rmdp-disabled'));
  disabledElements.forEach((element) => {
    element.removeAttribute('tabindex');
  });

  const keyboardElements = Array.from(
    calendarOverlayElement.querySelectorAll(keyboardTabbableSelector),
  );

  keyboardElements.forEach((element) => {
    if (element.classList.contains('rmdp-day-hidden') || element.classList.contains('rmdp-disabled')) {
      element.removeAttribute('tabindex');
      return;
    }
    element.setAttribute('tabindex', '0');
    if (element.getAttribute(keydownHandledAttribute) !== 'true') {
      element.addEventListener('keydown', (event) => {
        onDatePickerElementKeyDown(element, event);
      });
      element.setAttribute(keydownHandledAttribute, 'true');
    }
  });
};

const getNextFocusableElement = (focusableElements, activeElement, moveBackward) => {
  if (focusableElements.length === 0) {
    return null;
  }

  const currentIndex = focusableElements.indexOf(activeElement);
  if (currentIndex === -1) {
    return moveBackward ? focusableElements[focusableElements.length - 1] : focusableElements[0];
  }

  if (moveBackward) {
    return currentIndex === 0
      ? focusableElements[focusableElements.length - 1]
      : focusableElements[currentIndex - 1];
  }

  return currentIndex === focusableElements.length - 1
    ? focusableElements[0]
    : focusableElements[currentIndex + 1];
};

const getFirstVisiblePickerElement = (overlayElement) => {
  const yearPicker = overlayElement.querySelector('.rmdp-year-picker');
  const monthPicker = overlayElement.querySelector('.rmdp-month-picker');
  const dayPicker = overlayElement.querySelector('.rmdp-day-picker');

  const isPickerInBlockDisplay = (pickerElement) => (
    pickerElement
    && pickerElement.style
    && pickerElement.style.display === 'block'
    && isVisible(pickerElement)
  );

  let activePicker = dayPicker;
  if (isPickerInBlockDisplay(yearPicker)) {
    activePicker = yearPicker;
  } else if (isPickerInBlockDisplay(monthPicker)) {
    activePicker = monthPicker;
  }

  if (!activePicker || !isVisible(activePicker)) {
    return null;
  }

  return activePicker.querySelector(
    // eslint-disable-next-line max-len
    '.rmdp-day.rmdp-selected:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-day.rmdp-today:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-month:not(.rmdp-disabled), .rmdp-year:not(.rmdp-disabled)',
  );
};

const getFirstPickerGridElement = (overlayElement) => {
  const yearPicker = overlayElement.querySelector('.rmdp-year-picker');
  const monthPicker = overlayElement.querySelector('.rmdp-month-picker');
  const dayPicker = overlayElement.querySelector('.rmdp-day-picker');

  const isPickerInBlockDisplay = (pickerElement) => (
    pickerElement
    && pickerElement.style
    && pickerElement.style.display === 'block'
    && isVisible(pickerElement)
  );

  let activePicker = dayPicker;
  if (isPickerInBlockDisplay(yearPicker)) {
    activePicker = yearPicker;
  } else if (isPickerInBlockDisplay(monthPicker)) {
    activePicker = monthPicker;
  }

  if (!activePicker || !isVisible(activePicker)) {
    return null;
  }

  return activePicker.querySelector(
    '.rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-month:not(.rmdp-disabled), .rmdp-year:not(.rmdp-disabled)',
  );
};

const focusOverlay = (calendarOverlayElement) => {
  ensureKeyboardTabbableElements(calendarOverlayElement);

  if (!calendarOverlayElement.hasAttribute('tabindex')) {
    calendarOverlayElement.setAttribute('tabindex', '-1');
  }

  const focusableElements = getFocusableElements(calendarOverlayElement);
  if (focusableElements.length > 0) {
    const selectedOrActiveDay = calendarOverlayElement.querySelector(
      '.rmdp-day.rmdp-selected:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-day.rmdp-today:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled)',
    );
    if (selectedOrActiveDay && isVisible(selectedOrActiveDay)) {
      selectedOrActiveDay.focus();
      return;
    }
    focusableElements[0].focus();
  } else {
    calendarOverlayElement.focus();
  }
};

export const trapActiveDatePickerOverlayFocus = ({ onEscape } = {}) => {
  let focusRetryTimeout = null;
  let calendarMutationObserver = null;

  const attemptInitialFocus = (retriesLeft = 12) => {
    const calendarOverlayElement = getActiveCalendarOverlay();
    if (calendarOverlayElement) {
      focusOverlay(calendarOverlayElement);
      if (!calendarMutationObserver) {
        calendarMutationObserver = new MutationObserver(() => {
          ensureKeyboardTabbableElements(calendarOverlayElement);
        });
        calendarMutationObserver.observe(calendarOverlayElement, {
          childList: true,
          subtree: true,
        });
      }
      return;
    }

    if (retriesLeft <= 0) {
      return;
    }

    focusRetryTimeout = setTimeout(() => {
      attemptInitialFocus(retriesLeft - 1);
    }, 30);
  };

  const handleKeyDown = (event) => {
    const calendarOverlayElement = getActiveCalendarOverlay();
    if (!calendarOverlayElement) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (onEscape) {
        onEscape();
      }
      return;
    }

    ensureKeyboardTabbableElements(calendarOverlayElement);

    if (event.key !== 'Tab') {
      return;
    }

    if (
      !event.shiftKey
      && document.activeElement
      && document.activeElement.classList
      && document.activeElement.classList.contains('rmdp-right')
    ) {
      const nextPickerElement = getFirstVisiblePickerElement(calendarOverlayElement);
      if (nextPickerElement && isVisible(nextPickerElement)) {
        event.preventDefault();
        nextPickerElement.focus();
        return;
      }
    }

    if (event.shiftKey) {
      const firstPickerItem = getFirstPickerGridElement(calendarOverlayElement);
      const rightChevron = calendarOverlayElement.querySelector('.rmdp-right');
      if (
        firstPickerItem
        && rightChevron
        && document.activeElement === firstPickerItem
        && isVisible(rightChevron)
      ) {
        event.preventDefault();
        rightChevron.focus();
        return;
      }
    }

    const focusableElements = getFocusableElements(calendarOverlayElement);
    event.preventDefault();

    if (focusableElements.length === 0) {
      calendarOverlayElement.focus();
      return;
    }

    const { activeElement } = document;
    const nextFocusableElement = getNextFocusableElement(
      focusableElements,
      calendarOverlayElement.contains(activeElement) ? activeElement : null,
      event.shiftKey,
    );

    if (nextFocusableElement) {
      nextFocusableElement.focus();
    }
  };

  const handleFocusIn = (event) => {
    const calendarOverlayElement = getActiveCalendarOverlay();
    if (!calendarOverlayElement) {
      return;
    }

    if (calendarOverlayElement.contains(event.target)) {
      return;
    }

    ensureKeyboardTabbableElements(calendarOverlayElement);
    const focusableElements = getFocusableElements(calendarOverlayElement);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      calendarOverlayElement.focus();
    }
  };

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('focusin', handleFocusIn, true);
  attemptInitialFocus();

  return () => {
    if (focusRetryTimeout) {
      clearTimeout(focusRetryTimeout);
    }
    if (calendarMutationObserver) {
      calendarMutationObserver.disconnect();
    }
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('focusin', handleFocusIn, true);
  };
};
